import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
} from "@mui/material";
import ConfirmDialog from "components/ConfirmDialog";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { TableCellType } from "components/LazyTable";
import ProgressionGateForm from "components/ProgressionGateForm";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ListRequestOfSkuProgressionGateFilter,
  ListResponseOfSkuProgressionGate,
  SkuProgressionGate,
  SkuProgressionGateRequest,
} from "services/KnowfullyAdminClient";
import {
  ProgressionGateStatus,
  ProgressionGateTypes,
  SnackbarMessageProps,
} from "types/common-types";
import {
  SkuFormProps,
  SkuProgressionGateWithId,
} from "types/sku-related-types";
import {
  getProgressionGateStatus,
  isNoneGateType,
} from "utils/progressionGateUtils";

const SkuProgressionGates = ({ sku, ...props }: SkuFormProps) => {
  const { skuService } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const [createOpen, createOpenActions] = useBoolean(false);
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<SkuProgressionGate | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [
    skuProgressionGateToEdit,
    setSkuProgressionGateToEdit,
  ] = useState<SkuProgressionGate | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const createDeactivationGate = (skuId: string) =>
    SkuProgressionGateRequest.fromJS({
      skuId,
      organizationId: sku.ownerOrganizationId,
      skuFormatId: sku.skuFormatId,
      progressionGateTypeId: ProgressionGateTypes.None,
      promptMessage: "N/A",
      labelText: "N/A",
      value: "N/A",
      dialogueTitle: "N/A",
      errorMessage: "N/A",
    });

  const { data, isFetching } = useStrictQuery<ListResponseOfSkuProgressionGate>(
    ["skuProgressionGates", sku.id!],
    () =>
      skuService.getSkuProgressionGates(
        sku.id!,
        ListRequestOfSkuProgressionGateFilter.fromJS({
          sortField: "createdOn",
          sortDescending: true,
        })
      )
  );

  const skuProgressionGatesData = useMemo(
    () => (data?.data ?? []) as SkuProgressionGateWithId[],
    [data?.data]
  );

  const gateStatus = useMemo(
    () => getProgressionGateStatus(skuProgressionGatesData),
    [skuProgressionGatesData]
  );

  const isInherited = useMemo(
    () => gateStatus === ProgressionGateStatus.Inherited,
    [gateStatus]
  );
  const isDeactivated = useMemo(
    () => gateStatus === ProgressionGateStatus.Deactivated,
    [gateStatus]
  );

  const noneGate = useMemo(() => {
    return skuProgressionGatesData.find(
      (gate) =>
        gate.progressionGateTypeId && isNoneGateType(gate.progressionGateTypeId)
    );
  }, [skuProgressionGatesData]);

  const DeleteButton = ({ value }: { value: SkuProgressionGate }) => {
    if (isInherited) return null;
    return (
      <Box textAlign="center">
        <IconButton size="small" onClick={() => setDeleting(value)}>
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const EditButton = ({ value }: { value: SkuProgressionGate }) => {
    if (isInherited) {
      return null;
    }
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          onClick={() => {
            setSkuProgressionGateToEdit(value);
            createOpenActions.setTrue();
          }}
        >
          <EditIcon />
        </IconButton>
      </Box>
    );
  };

  const handleDeactivate = async () => {
    if (!sku.id) {
      return;
    }

    await skuService.saveSkuProgressionGate(createDeactivationGate(sku.id));

    queryClient.invalidateQueries(["skuProgressionGates", sku.id]);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "progressionGateDeactivated" }))
    );
    setIsDeactivating(false);
  };

  const handleReactivate = async () => {
    if (!noneGate?.id) {
      return;
    }

    await skuService.deleteSkuProgressionGate(noneGate.id);

    queryClient.invalidateQueries(["skuProgressionGates", sku.id!]);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "progressionGateReactivated" }))
    );
  };

  const handleDelete = async () => {
    const id = deleting?.id;
    setDeleting(null);

    if (!id) {
      return;
    }

    await skuService.deleteSkuProgressionGate(id);

    queryClient.invalidateQueries(["skuProgressionGates", sku.id!]);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "courseProgressionGateDeleted" }))
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "progressionGateTypeName",
      align: "left",
      type: "text",
      label: t({ id: "gateTypeLabel" }),
    },
    {
      accessor: "dialogueTitle",
      align: "left",
      type: "text",
      label: t({ id: "dialogueTitleLabel" }),
    },
    {
      accessor: "prompt",
      align: "left",
      type: "text",
      label: t({ id: "promptLabel" }),
    },
    {
      accessor: "label",
      align: "left",
      type: "text",
      label: t({ id: "placeholderLabel" }),
    },
    {
      accessor: "value",
      align: "left",
      type: "text",
      label: t({ id: "courseProgressionRequiredResponseLabel" }),
    },
    {
      accessor: "errorMessage",
      align: "left",
      type: "text",
      label: t({ id: "messageLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: EditButton,
      label: "",
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  const onFormClose = () => {
    createOpenActions.setFalse();
    setSkuProgressionGateToEdit(null);
  };

  const onFormUpdate = () => {
    displaySnackbarMessage(t({ id: "courseProgressionGateUpdated" }));
  };

  const onFormCreate = () => {
    displaySnackbarMessage(t({ id: "courseProgressionGateSaved" }));
  };

  const displaySnackbarMessage = (message: string) => {
    queryClient.invalidateQueries(["skuProgressionGates", sku.id!]);
    createOpenActions.setFalse();
    setSkuProgressionGateToEdit(null);
    setSnackbarMessage(new SnackbarMessageProps(message));
  };

  const getInfoMessage = () => {
    switch (gateStatus) {
      case ProgressionGateStatus.Deactivated:
        return t({ id: "progressionGateDisabledInfo" });
      case ProgressionGateStatus.Inherited:
        return t({ id: "progressionGateInheritedInfo" });
      case ProgressionGateStatus.Overridden:
        return t({ id: "progressionGateOverriddenInfo" });
      default:
        return null;
    }
  };

  const getButtonLabel = () => {
    if (isDeactivated) {
      return null;
    }
    if (isInherited) {
      return t({ id: "progressionGateOverrideCta" });
    }
    return t({ id: "progressionGatesAddCta" });
  };

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid item xs={12} container spacing={2} justifyContent="flex-end">
          {isDeactivated ? (
            <Grid item>
              <Button variant="outlined" onClick={handleReactivate}>
                {t({ id: "reactivateProgressionGate" })}
              </Button>
            </Grid>
          ) : (
            <>
              {skuProgressionGatesData.length > 0 && (
                <Grid item>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setIsDeactivating(true)}
                  >
                    {t({ id: "progressionGateDeactivateCta" })}
                  </Button>
                </Grid>
              )}
              <Grid item>
                <Button variant="outlined" onClick={createOpenActions.setTrue}>
                  {getButtonLabel()}
                </Button>
              </Grid>
            </>
          )}
        </Grid>

        <Grid item xs={12}>
          {getInfoMessage() && (
            <Box mb={2}>
              <Alert severity="info">{getInfoMessage()}</Alert>
            </Box>
          )}

          {!isDeactivated && (
            <SortableTable
              {...{
                rows: skuProgressionGatesData,
                loading: isFetching,
                dense: true,
                cells,
                tableRowPerPage: 10,
              }}
            />
          )}
        </Grid>
      </Grid>

      <Dialog open={createOpen} onClose={onFormClose} maxWidth="md" fullWidth>
        <ProgressionGateForm
          {...{
            onClose: onFormClose,
            onUpdate: onFormUpdate,
            onCreate: onFormCreate,
            sku,
            progressionGate: skuProgressionGateToEdit,
          }}
        />
      </Dialog>

      <DeleteEntityDialog
        dialogTitleKey="areYouSure"
        rawContent={t({ id: "courseProgressionGateDeleteQuestion" })}
        onCancel={() => setDeleting(null)}
        handleDelete={handleDelete}
        open={!!deleting}
      />

      <ConfirmDialog
        dialogTitleKey="areYouSure"
        rawContent={t({ id: "deactivateProgressionGateConfirmation" })}
        onCancel={() => setIsDeactivating(false)}
        handleConfirm={handleDeactivate}
        open={isDeactivating}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default withErrorBoundary(SkuProgressionGates);
